import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
// @mui
import { Stack, IconButton, InputAdornment, TextField, Typography, Button, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-hot-toast';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
// components
import { Auth } from 'aws-amplify';
import Iconify from '../../../components/iconify';
import { loginValidate } from './loginValidate';
import { signInUser } from './signInUser';
import { newPasswordValidate } from './newPasswordValidate';
import { updateUserOnExternalDb } from '../../../store/actions/user/updateUserOnRegister';
import { getUserInfo } from '../../../store/actions/user/unAuthUser';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setname] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordResetLoading, setPasswordLoading] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [resetPassword, setResetPassword] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    const err = loginValidate(email, password);
    if (!err.message) {
      const result = await signInUser(email, password, dispatch);
      if (result && result.success === true) {
        if (result.user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setLoading(false);
          setResetPassword(true);
        } else {
          const userInfoResponse = await getUserInfo(email, dispatch);
          // console.log('userInfoRes ddddd', userInfoResponse);
          if (userInfoResponse && userInfoResponse.success === true) {
            navigate('/app');
          } else {
            await Auth.signOut();
            setLoading(false);
            toast.error('Error logging in. Please try again.');
          }
        }
      } else {
        setLoading(false);
        toast.error(result.error.message || 'Error loggin in! Please try again');
      }
    } else {
      // console.log('Error exists');
      toast.error(err.message);
    }
  };

  const resetPasswordHandler = async () => {
    setPasswordLoading(true);
    const err = newPasswordValidate(name, newPassword, newPasswordConfirm);
    if (!err.message) {
      const result = await signInUser(email, password, dispatch);
      if (result && result.success === true) {
        if (result.user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          const loggedUser = await Auth.completeNewPassword(result.user, newPassword, {
            name,
          });
          if (
            loggedUser &&
            loggedUser?.signInUserSession &&
            loggedUser?.signInUserSession?.idToken &&
            loggedUser?.signInUserSession?.idToken?.payload
          ) {
            const email = loggedUser?.signInUserSession?.idToken?.payload?.email;
            const userId = loggedUser?.signInUserSession?.idToken?.payload?.sub;
            const updateUserExternalOutput = await updateUserOnExternalDb(email, userId, name);
            if (updateUserExternalOutput && updateUserExternalOutput === true) {
              setPasswordLoading(true);
              navigate('/app');
              toast.success('Password successfully changed!');
            } else {
              toast.error('1. Error updating your account. Please contact us for help');
              await Auth.signOut();
              setPasswordLoading(false);
            }
          } else {
            toast.error('2. Error updating your account. Please contact us for help');
            await Auth.signOut();
            setPasswordLoading(false);
          }
        }
      } else {
        toast.error(result.error.message || 'Error loggin in! Please try again');
        setPasswordLoading(false);
      }
    } else {
      toast.error(err.message);
      setPasswordLoading(false);
    }
  };

  return !resetPassword ? (
    <>
      <Box style={{ marginTop: '10px' }}>
        <Button component={Link} to="/">
          <KeyboardBackspaceIcon style={{ marginRight: '8px' }} />
          Back
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom>
        MedAffairs AI
      </Typography>
      <Typography variant="h7" sx={{ mb: 3 }} gutterBottom>
        Real-time Medical Affairs Insights When You Need It{' '}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Don’t have an account? {''}
        <Link to="/demo" variant="subtitle2">
          Contact us to get started
        </Link>
      </Typography>
      <Stack spacing={3}>
        <TextField name="email" label="Email address" onChange={(e) => setEmail(e.target.value)} />

        <TextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <Button component={Link} to="/forgot-password">
          Forgot Password?
        </Button>
      </Stack>
      <LoadingButton
        loading={loading}
        fullWidth
        size="large"
        type="submit"
        variant="outlined"
        onClick={handleClick}
        sx={{
          backgroundColor: '#021f28',
        }}
      >
        Login
      </LoadingButton>
    </>
  ) : (
    <>
      <Typography variant="h4" gutterBottom>
        <LockIcon /> Password Reset Required
      </Typography>
      <Typography variant="body2" sx={{ mb: 5 }}>
        Create a password that is both strong and easy to remember
      </Typography>
      <form noValidate>
        <Stack spacing={3}>
          <TextField
            inputProps={{
              autoComplete: 'off',
            }}
            name="full name"
            label="Full Name"
            onChange={(e) => setname(e.target.value)}
          />

          <TextField
            name="password"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              autoComplete: 'new-password',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            name="password"
            label="New Password Confirm"
            type={showPassword ? 'text' : 'password'}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
            InputProps={{
              autoComplete: 'new-password',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </form>
      <LoadingButton
        sx={{ mt: 3 }}
        loading={passwordResetLoading}
        fullWidth
        size="large"
        type="submit"
        variant="outlined"
        onClick={resetPasswordHandler}
      >
        Change Password
      </LoadingButton>{' '}
    </>
  );
}
